import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  AuthenticitySignal,
  ReceiptDto,
  ReceiptStatus,
  ReviewReceiptInput,
  TrafficLight,
  useReceipt,
  useReceipts,
  useReopenReceipt,
  useReviewReceipt,
  useSetReceiptSponsor,
} from '@shared/api/hooks/useReceipts';
import { useSponsors } from '@shared/api/hooks/useSponsors';

const STATUS_LABELS: Record<ReceiptStatus, string> = {
  PENDING: 'In coda',
  PROCESSING: 'Elaborazione AI',
  APPROVED: 'Approvato',
  REJECTED: 'Respinto',
  REVIEW: 'Revisione manuale',
};

const STATUS_COLORS: Record<ReceiptStatus, string> = {
  PENDING: 'default',
  PROCESSING: 'blue',
  APPROVED: 'green',
  REJECTED: 'red',
  REVIEW: 'orange',
};

// --- Antifrode helpers ---

const LIGHT_COLOR: Record<TrafficLight, string> = {
  GREEN: 'green',
  YELLOW: 'gold',
  RED: 'red',
};

const LIGHT_LABEL: Record<TrafficLight, string> = {
  GREEN: 'Verde',
  YELLOW: 'Giallo',
  RED: 'Rosso',
};

/** Piccolo badge semaforo con etichetta. */
function TrafficLightBadge({
  light,
  label,
}: {
  light: TrafficLight | undefined;
  label: string;
}): React.ReactElement {
  if (!light) {
    return (
      <Space direction="vertical" size={2} style={{ textAlign: 'center' }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {label}
        </Typography.Text>
        <Tag style={{ margin: 0 }}>—</Tag>
      </Space>
    );
  }
  return (
    <Space direction="vertical" size={2} style={{ textAlign: 'center' }}>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {label}
      </Typography.Text>
      <Tag color={LIGHT_COLOR[light]} style={{ margin: 0, fontWeight: 600 }}>
        {LIGHT_LABEL[light]}
      </Tag>
    </Space>
  );
}

/** Tabella segnali di veridicità (solo admin, opzionale). */
function AuthenticitySignalsList({
  signals,
}: {
  signals: AuthenticitySignal[] | undefined;
}): React.ReactElement | null {
  if (!signals || signals.length === 0) return null;
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text strong style={{ fontSize: 12 }}>
        Segnali veridicità
      </Typography.Text>
      <Table<AuthenticitySignal>
        size="small"
        pagination={false}
        rowKey="code"
        dataSource={signals}
        columns={[
          { title: 'Codice', dataIndex: 'code', width: 160, render: (v: string) => <code>{v}</code> },
          {
            title: 'Peso',
            dataIndex: 'weight',
            width: 70,
            align: 'right',
            render: (v: number) => (
              <Tag color={v < 0 ? 'red' : v > 0 ? 'green' : 'default'}>
                {v > 0 ? `+${v}` : v}
              </Tag>
            ),
          },
          { title: 'Dettaglio', dataIndex: 'detail' },
        ]}
      />
    </Space>
  );
}

const REJECT_REASON_PRESETS = [
  'Scontrino illeggibile',
  'Dati mancanti',
  'Scontrino troncato',
  'P.IVA non riconosciuta',
  'Importo non leggibile',
  'Scontrino duplicato',
  'Sponsor non aderente',
  'Scontrino più vecchio di 30 giorni',
];

const MAX_RECEIPT_AGE_DAYS = 30;

const STATUS_FILTER_OPTIONS = [
  { label: 'Tutti', value: 'ALL' },
  { label: 'Da rivedere', value: 'REVIEW' },
  { label: 'Approvati', value: 'APPROVED' },
  { label: 'Respinti', value: 'REJECTED' },
  { label: 'AI in corso', value: 'PROCESSING' },
];

interface ApproveFormValues {
  pointsAwarded: number;
  sponsorId?: string;
  aiStoreName?: string;
  aiPartitaIva?: string;
  aiBusinessName?: string;
  aiDate?: dayjs.Dayjs;
  aiTotalAmount?: number;
  aiReceiptNumber?: string;
}

interface RejectFormValues {
  rejectionReason: string;
}

// T-138 — form della riapertura di uno scontrino respinto.
interface ReopenFormValues {
  reason: string;
}

function buildOverrides(
  v: ApproveFormValues,
  original: ReceiptDto | undefined,
): Partial<ReviewReceiptInput> {
  const out: Partial<ReviewReceiptInput> = {};
  if (!original) return out;
  if ((v.aiStoreName ?? '') !== (original.aiStoreName ?? '')) out.aiStoreName = v.aiStoreName;
  if ((v.aiPartitaIva ?? '') !== (original.aiPartitaIva ?? '')) out.aiPartitaIva = v.aiPartitaIva;
  if ((v.aiBusinessName ?? '') !== (original.aiBusinessName ?? ''))
    out.aiBusinessName = v.aiBusinessName;
  if ((v.aiReceiptNumber ?? '') !== (original.aiReceiptNumber ?? ''))
    out.aiReceiptNumber = v.aiReceiptNumber;
  const dateStr = v.aiDate?.format('YYYY-MM-DD');
  if ((dateStr ?? '') !== (original.aiDate ?? '')) out.aiDate = dateStr;
  if (
    v.aiTotalAmount != null &&
    Number(v.aiTotalAmount.toFixed(2)) !== Number((original.aiTotalAmount ?? 0).toFixed(2))
  ) {
    out.aiTotalAmount = Number(v.aiTotalAmount);
  }
  // T-155 — il partner non è un campo estratto dall'AI, ma viaggia nello stesso
  // payload. Si manda solo se l'admin l'ha scelto/cambiato: se resta quello già
  // agganciato, lascia decidere la ri-risoluzione lato server.
  if (v.sponsorId && v.sponsorId !== (original.sponsorId ?? undefined)) {
    out.sponsorId = v.sponsorId;
  }
  return out;
}

export function ReceiptsPage(): React.ReactElement {
  const [filterStatus, setFilterStatus] = useState<ReceiptStatus | 'ALL'>('REVIEW');
  const [filterSponsor, setFilterSponsor] = useState<string | undefined>(undefined);
  const [filterSearch, setFilterSearch] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [approveId, setApproveId] = useState<string | null>(null);
  // T-155 — il rifiuto per partner mancante è lungo e dice cosa fare: un toast
  // lo taglia, quindi resta scritto dentro la modale finché non si risolve.
  const [approveError, setApproveError] = useState<string | null>(null);
  const [reopenId, setReopenId] = useState<string | null>(null); // T-138
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [rejectForm] = Form.useForm<RejectFormValues>();
  const [approveForm] = Form.useForm<ApproveFormValues>();
  const [bulkRejectForm] = Form.useForm<RejectFormValues>();
  const [reopenForm] = Form.useForm<ReopenFormValues>(); // T-138

  const { data: sponsorsData } = useSponsors();
  const sponsorOptions = (sponsorsData?.data ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));
  // T-155 — nel selettore dell'approvazione vanno solo i partner attivi: un
  // partner disattivato non aggancia lato server, e offrirlo qui porterebbe
  // l'admin dritto in un rifiuto.
  const activeSponsorOptions = (sponsorsData?.data ?? [])
    .filter((s) => s.isActive)
    .map((s) => ({ value: s.id, label: s.name }));

  const apiStatus = filterStatus === 'ALL' ? undefined : filterStatus;
  const {
    data: pageData,
    isLoading,
    refetch,
    isFetching,
  } = useReceipts({
    status: apiStatus,
    sponsorId: filterSponsor,
    search: filterSearch,
    page,
    limit: 20,
  });
  const { data: detail } = useReceipt(drawerId);
  const reviewMutation = useReviewReceipt();
  const reopenMutation = useReopenReceipt(); // T-138
  const setSponsorMutation = useSetReceiptSponsor(); // T-266
  // T-266 — partner scelto nell'editor del drawer (indipendente dall'approvazione).
  const [editSponsorId, setEditSponsorId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setEditSponsorId(detail?.sponsorId ?? undefined);
  }, [detail?.id, detail?.sponsorId]);

  const onSaveSponsor = async (): Promise<void> => {
    if (!detail || !editSponsorId) return;
    try {
      await setSponsorMutation.mutateAsync({ id: detail.id, sponsorId: editSponsorId });
      message.success('Partner aggiornato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore');
    }
  };

  // T-138 — conteggio della coda REVIEW (oggi in prod e' 0): serve a segnalare
  // all'admin che ci sono scontrini riaperti in attesa, altrimenti li riapriamo
  // e restano fermi in una lista che nessuno guarda.
  const { data: reviewCountData } = useReceipts({ status: 'REVIEW', limit: 1 });
  const reviewCount = reviewCountData?.meta.total ?? 0;

  const approveTarget = pageData?.data.find((r) => r.id === approveId);
  const reopenTarget = pageData?.data.find((r) => r.id === reopenId); // T-138

  const openApprove = (r: ReceiptDto): void => {
    approveForm.resetFields();
    // Loyalty rule: 1 EUR = 1 punto, arrotondato. Fallback 10pt if no amount.
    const computed = r.aiTotalAmount != null ? Math.max(1, Math.round(r.aiTotalAmount)) : 10;
    approveForm.setFieldsValue({
      pointsAwarded: r.pointsAwarded > 0 ? r.pointsAwarded : computed,
      aiStoreName: r.aiStoreName ?? undefined,
      aiPartitaIva: r.aiPartitaIva ?? undefined,
      aiBusinessName: r.aiBusinessName ?? undefined,
      aiDate: r.aiDate ? dayjs(r.aiDate) : undefined,
      aiTotalAmount: r.aiTotalAmount ?? undefined,
      aiReceiptNumber: r.aiReceiptNumber ?? undefined,
      sponsorId: r.sponsorId ?? undefined,
    });
    setApproveError(null);
    setApproveId(r.id);
  };

  const onApprove = async (): Promise<void> => {
    if (!approveId) return;
    const v = await approveForm.validateFields();
    try {
      const overrides = buildOverrides(v, approveTarget);
      await reviewMutation.mutateAsync({
        id: approveId,
        input: { decision: 'APPROVE', pointsAwarded: v.pointsAwarded, ...overrides },
      });
      message.success(`Approvato (+${v.pointsAwarded}pt)`);
      setApproveError(null);
      setApproveId(null);
      approveForm.resetFields();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      const text = Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore');
      setApproveError(text);
      message.error(text);
    }
  };

  const onReject = async (): Promise<void> => {
    if (!rejectId) return;
    const v = await rejectForm.validateFields();
    try {
      await reviewMutation.mutateAsync({
        id: rejectId,
        input: { decision: 'REJECT', rejectionReason: v.rejectionReason },
      });
      message.success('Respinto');
      setRejectId(null);
      rejectForm.resetFields();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  // T-138 — riapertura di uno scontrino RESPINTO (→ REVISIONE). NON approva.
  const onReopen = async (): Promise<void> => {
    if (!reopenId) return;
    const v = await reopenForm.validateFields();
    try {
      await reopenMutation.mutateAsync({ id: reopenId, input: { reason: v.reason } });
      message.success('Scontrino riaperto: ora è in coda di revisione');
      setReopenId(null);
      reopenForm.resetFields();
    } catch (e) {
      // L'errore del server (es. 409 se non è più RESPINTO) va mostrato per intero.
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Riapertura non riuscita'));
    }
  };

  const onBulkReject = async (): Promise<void> => {
    const v = await bulkRejectForm.validateFields();
    setBulkLoading(true);
    try {
      const ids = selectedRowKeys.map(String);
      const results = await Promise.allSettled(
        ids.map((id) =>
          reviewMutation.mutateAsync({
            id,
            input: { decision: 'REJECT', rejectionReason: v.rejectionReason },
          }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        message.warning(`${ids.length - failed} respinti, ${failed} falliti`);
      } else {
        message.success(`${ids.length} scontrini respinti`);
      }
      setBulkRejectOpen(false);
      setSelectedRowKeys([]);
      bulkRejectForm.resetFields();
    } finally {
      setBulkLoading(false);
    }
  };

  const onMarkReview = async (id: string): Promise<void> => {
    try {
      await reviewMutation.mutateAsync({ id, input: { decision: 'REVIEW' } });
      message.success('Marcato per revisione manuale');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  const data = pageData?.data ?? [];
  const meta = pageData?.meta;

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Scontrini
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="Cerca tifoso, email o negozio"
            style={{ minWidth: 220 }}
            defaultValue={filterSearch}
            onSearch={(v) => {
              setFilterSearch(v || undefined);
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="Filtra per partner"
            style={{ minWidth: 220 }}
            options={sponsorOptions}
            value={filterSponsor}
            onChange={(v) => {
              setFilterSponsor(v);
              setPage(1);
            }}
          />
          <Segmented
            // T-138 — badge con il numero di scontrini in attesa di revisione.
            options={STATUS_FILTER_OPTIONS.map((o) =>
              o.value === 'REVIEW' && reviewCount > 0
                ? {
                    ...o,
                    label: (
                      <Badge count={reviewCount} size="small" offset={[10, -2]}>
                        {o.label}
                      </Badge>
                    ),
                  }
                : o,
            )}
            value={filterStatus}
            onChange={(v) => {
              setFilterStatus(v as ReceiptStatus | 'ALL');
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Solo gli scontrini dei partner ufficiali danno punti"
        description={
          <>
            Uno scontrino è accettato solo se la P.IVA o la ragione sociale corrisponde a una di
            quelle registrate. Gestisci brand, ragioni sociali e P.IVA accettate nella pagina{' '}
            <Link to="/sponsors">Sponsor locali</Link>.
          </>
        }
      />
      {/* T-138 — se ci sono scontrini in attesa di revisione ma l'admin sta
          guardando un altro filtro, glielo segnaliamo: i riaperti non devono
          restare fermi in una coda che nessuno apre. */}
      {reviewCount > 0 && filterStatus !== 'REVIEW' ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${reviewCount} scontrini in attesa di revisione manuale`}
          action={
            <Button
              size="small"
              onClick={() => {
                setFilterStatus('REVIEW');
                setPage(1);
              }}
            >
              Vai alla coda
            </Button>
          }
        />
      ) : null}
      {selectedRowKeys.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space>
              <span>{selectedRowKeys.length} scontrini selezionati</span>
              <Button danger size="small" onClick={() => setBulkRejectOpen(true)}>
                Respingi selezionati
              </Button>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                Deseleziona
              </Button>
            </Space>
          }
        />
      ) : null}
      <Table<ReceiptDto>
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          getCheckboxProps: (r) => ({
            disabled: r.status === 'APPROVED' || r.status === 'REJECTED',
          }),
        }}
        onRow={(r) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement;
            if (target.closest('.ant-checkbox-wrapper, .ant-table-selection-column')) return;
            setDrawerId(r.id);
          },
          style: { cursor: 'pointer' },
        })}
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        columns={[
          {
            title: 'Data',
            dataIndex: 'createdAt',
            width: 150,
            responsive: ['md'],
            render: (v: string, r) => {
              const isExpired =
                r.aiDate != null &&
                dayjs().diff(dayjs(r.aiDate), 'day') > MAX_RECEIPT_AGE_DAYS;
              return (
                <Space direction="vertical" size={0}>
                  <Typography.Text>{dayjs(v).format('DD/MM/YY HH:mm')}</Typography.Text>
                  {r.aiDate ? (
                    <Tooltip
                      title={
                        isExpired
                          ? `Scontrino datato ${dayjs(r.aiDate).format(
                              'DD/MM/YYYY',
                            )} — oltre i ${MAX_RECEIPT_AGE_DAYS} giorni`
                          : 'Data scontrino'
                      }
                    >
                      <Typography.Text
                        type={isExpired ? 'danger' : 'secondary'}
                        style={{ fontSize: 11 }}
                      >
                        {dayjs(r.aiDate).format('DD/MM/YYYY')}
                      </Typography.Text>
                    </Tooltip>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      —
                    </Typography.Text>
                  )}
                </Space>
              );
            },
          },
          {
            title: 'Tifoso',
            width: 200,
            render: (_v, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {r.userDisplayName ?? <Typography.Text type="secondary">—</Typography.Text>}
                </Typography.Text>
                {r.userEmail ? (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {r.userEmail}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: 'Scontrino',
            width: 200,
            render: (_v, r) =>
              r.aiStoreName ? (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{r.aiStoreName}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    P.IVA: {r.aiPartitaIva ?? '—'}
                  </Typography.Text>
                  <Space size={8}>
                    <Typography.Text style={{ fontSize: 11 }}>
                      {r.aiTotalAmount != null ? `€ ${r.aiTotalAmount.toFixed(2)}` : '—'}
                    </Typography.Text>
                    {r.aiReceiptNumber ? (
                      <Typography.Text
                        type="secondary"
                        style={{ fontFamily: 'monospace', fontSize: 11 }}
                      >
                        N° {r.aiReceiptNumber}
                      </Typography.Text>
                    ) : null}
                  </Space>
                </Space>
              ) : (
                <Typography.Text type="secondary">— in elaborazione —</Typography.Text>
              ),
          },
          {
            title: 'Partner',
            dataIndex: 'sponsorName',
            width: 150,
            responsive: ['lg'],
            render: (v: string | null) =>
              v ? (
                <Tag color="geekblue">{v}</Tag>
              ) : (
                <Typography.Text type="secondary">— nessuno —</Typography.Text>
              ),
          },
          {
            title: (
              <Tooltip title="Affidabilità: confidenza lettura AI (R) + veridicità antifrode (V)">
                Affidabilità
              </Tooltip>
            ),
            width: 110,
            align: 'center',
            render: (_v, r) => (
              <Space direction="vertical" size={2} style={{ alignItems: 'center' }}>
                {r.aiConfidence != null ? (
                  <Tag
                    color={r.aiConfidence >= 0.85 ? 'green' : r.aiConfidence >= 0.5 ? 'orange' : 'red'}
                    style={{ margin: 0 }}
                  >
                    R {Math.round(r.aiConfidence * 100)}%
                  </Tag>
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    R —
                  </Typography.Text>
                )}
                {r.authenticityLight ? (
                  <Tooltip
                    title={
                      r.authenticityScore != null
                        ? `Score V: ${r.authenticityScore}/100`
                        : undefined
                    }
                  >
                    <Tag
                      color={LIGHT_COLOR[r.authenticityLight]}
                      style={{ margin: 0, cursor: r.authenticityScore != null ? 'help' : 'default' }}
                    >
                      V {LIGHT_LABEL[r.authenticityLight]}
                      {r.authenticityScore != null ? ` ${r.authenticityScore}` : ''}
                    </Tag>
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    V —
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Stato',
            dataIndex: 'status',
            width: 150,
            render: (s: ReceiptStatus, r) => (
              <Space direction="vertical" size={0}>
                <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>
                {r.reviewSource === 'AI_AUTO' ? (
                  <Tag color="purple" style={{ marginInlineEnd: 0, fontSize: 10 }}>
                    🤖 AI auto
                  </Tag>
                ) : r.reviewSource === 'MANUAL' ? (
                  <Tag color="cyan" style={{ marginInlineEnd: 0, fontSize: 10 }}>
                    👤 Manuale
                  </Tag>
                ) : null}
                {s === 'REVIEW' && r.reviewSlaDueAt ? (
                  <Tooltip title={`Regolamento §5: evadere entro il ${dayjs(r.reviewSlaDueAt).format('DD/MM/YYYY')}`}>
                    <Tag
                      color={r.reviewSlaOverdue ? 'red' : 'default'}
                      style={{ marginInlineEnd: 0, fontSize: 10 }}
                    >
                      {r.reviewSlaOverdue
                        ? '⏰ SLA scaduta'
                        : `⏳ entro ${dayjs(r.reviewSlaDueAt).diff(dayjs(), 'day')}g`}
                    </Tag>
                  </Tooltip>
                ) : null}
              </Space>
            ),
          },
          {
            title: 'Punti',
            dataIndex: 'pointsAwarded',
            width: 80,
            align: 'right',
            render: (v: number) =>
              v > 0 ? <Typography.Text strong>+{v}</Typography.Text> : '—',
          },
          {
            title: 'Azioni',
            width: 210,
            align: 'right',
            fixed: 'right',
            render: (_v, r) => {
              // T-138 — un RESPINTO (a mano o dall'AI) puo' essere RIAPERTO da un
              // admin: torna in REVISIONE, non viene approvato. APPROVED resta
              // terminale (nessuna azione).
              if (r.status === 'REJECTED') {
                return (
                  <Space onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      icon={<RollbackOutlined />}
                      aria-label="Riapri scontrino respinto per revisione"
                      onClick={() => {
                        reopenForm.resetFields();
                        setReopenId(r.id);
                      }}
                    >
                      Riapri (→ revisione)
                    </Button>
                  </Space>
                );
              }
              if (r.status === 'APPROVED') return null;
              return (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    aria-label="Approva scontrino"
                    onClick={() => openApprove(r)}
                  >
                    Approva
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    aria-label="Respingi scontrino"
                    onClick={() => {
                      rejectForm.resetFields();
                      setRejectId(r.id);
                    }}
                  >
                    Respingi
                  </Button>
                  {r.status === 'PROCESSING' || r.status === 'PENDING' ? (
                    <Popconfirm
                      title="Marcare per revisione manuale?"
                      okText="Sì"
                      cancelText="No"
                      onConfirm={() => onMarkReview(r.id)}
                    >
                      <Button size="small" aria-label="Marca per revisione manuale">
                        Review
                      </Button>
                    </Popconfirm>
                  ) : null}
                </Space>
              );
            },
          },
        ]}
      />

      {/* Detail drawer */}
      <Drawer
        title={detail ? `Scontrino ${detail.id.slice(0, 8)}` : 'Scontrino'}
        placement="right"
        width={900}
        open={drawerId !== null}
        onClose={() => setDrawerId(null)}
      >
        {detail ? (
          <Row gutter={24}>
            <Col span={10}>
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div>
                  <Typography.Text strong>{detail.userDisplayName ?? '—'}</Typography.Text>
                  <br />
                  <Typography.Text type="secondary">{detail.userEmail ?? '—'}</Typography.Text>
                </div>
                {detail.imageDownloadUrl ? (
                  <img
                    src={detail.imageDownloadUrl}
                    alt="receipt"
                    style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                ) : (
                  <Typography.Text type="secondary">Nessuna immagine disponibile</Typography.Text>
                )}
              </Space>
            </Col>
            <Col span={14}>
              <Tabs
                items={[
                  {
                    key: 'ai',
                    label: 'Dati AI',
                    children: (
                      <Space direction="vertical" size={4}>
                        <Typography.Text>
                          <strong>Negozio:</strong> {detail.aiStoreName ?? '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>P.IVA:</strong> {detail.aiPartitaIva ?? '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>Ragione sociale:</strong> {detail.aiBusinessName ?? '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>Data:</strong> {detail.aiDate ?? '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>Totale:</strong>{' '}
                          {detail.aiTotalAmount != null
                            ? `€ ${detail.aiTotalAmount.toFixed(2)}`
                            : '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>Numero scontrino:</strong> {detail.aiReceiptNumber ?? '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>Confidence AI:</strong>{' '}
                          {detail.aiConfidence != null
                            ? `${Math.round(detail.aiConfidence * 100)}%`
                            : '—'}
                        </Typography.Text>
                        <Typography.Text>
                          <strong>Partner abbinato:</strong>{' '}
                          {detail.sponsorName ? (
                            <Tag color="geekblue">{detail.sponsorName}</Tag>
                          ) : (
                            <Typography.Text type="secondary">nessuno</Typography.Text>
                          )}
                        </Typography.Text>
                        {/* T-266 — cambia/assegna il partner su uno scontrino esistente.
                            Non ricalcola i punti; su uno approvato e' ri-attribuzione. */}
                        <Space.Compact style={{ width: '100%' }}>
                          <Select
                            id="receiptSponsorEditor"
                            showSearch
                            optionFilterProp="label"
                            style={{ flex: 1 }}
                            placeholder="Cambia / assegna partner"
                            options={activeSponsorOptions}
                            value={editSponsorId}
                            onChange={(v) => setEditSponsorId(v)}
                          />
                          <Button
                            type="primary"
                            loading={setSponsorMutation.isPending}
                            disabled={!editSponsorId || editSponsorId === (detail.sponsorId ?? undefined)}
                            onClick={onSaveSponsor}
                          >
                            Salva partner
                          </Button>
                        </Space.Compact>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Cambiare il partner non modifica i punti e non approva lo scontrino.
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  ...(detail.readingLight != null || detail.authenticityLight != null
                    ? [
                        {
                          key: 'antifraud',
                          label: 'Antifrode',
                          children: (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                              {/* Due semafori affiancati */}
                              <Space size={24}>
                                <TrafficLightBadge
                                  light={detail.readingLight}
                                  label="Lettura dati"
                                />
                                <Space direction="vertical" size={2} style={{ textAlign: 'center' }}>
                                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                    Veridicità
                                  </Typography.Text>
                                  {detail.authenticityLight ? (
                                    <Tag
                                      color={LIGHT_COLOR[detail.authenticityLight]}
                                      style={{ margin: 0, fontWeight: 600 }}
                                    >
                                      {LIGHT_LABEL[detail.authenticityLight]}
                                      {detail.authenticityScore != null
                                        ? ` — ${detail.authenticityScore}/100`
                                        : ''}
                                    </Tag>
                                  ) : (
                                    <Tag style={{ margin: 0 }}>—</Tag>
                                  )}
                                </Space>
                              </Space>

                              {/* Alert disparità: R alto + V basso = potenziale falso sofisticato */}
                              {detail.disparity != null && detail.disparity >= 50 && (
                                <Alert
                                  type="warning"
                                  showIcon
                                  message="Possibile scontrino artefatto"
                                  description={`Ben leggibile ma scarsa veridicità (R ${detail.aiConfidence != null ? Math.round(detail.aiConfidence * 100) : '?'}%, V ${detail.authenticityScore ?? '?'}). Disparità: ${detail.disparity}. Verificare manualmente prima di approvare.`}
                                />
                              )}

                              {/* Segnali di veridicità — solo admin */}
                              <AuthenticitySignalsList signals={detail.authenticitySignals} />
                            </Space>
                          ),
                        },
                      ]
                    : []),
                  {
                    key: 'status',
                    label: 'Stato',
                    children: (
                      <Space direction="vertical" size={4}>
                        <Space>
                          <Tag color={STATUS_COLORS[detail.status]}>
                            {STATUS_LABELS[detail.status]}
                          </Tag>
                          {detail.reviewSource === 'AI_AUTO' ? (
                            <Tag color="purple">🤖 Approvazione AI</Tag>
                          ) : detail.reviewSource === 'MANUAL' ? (
                            <Tag color="cyan">👤 Revisione manuale</Tag>
                          ) : null}
                        </Space>
                        {detail.pointsAwarded > 0 ? (
                          <Typography.Text>
                            <strong>Punti assegnati:</strong> +{detail.pointsAwarded}
                          </Typography.Text>
                        ) : null}
                        {detail.rejectionReason ? (
                          <Alert
                            message="Motivo rejection"
                            description={detail.rejectionReason}
                            type="error"
                          />
                        ) : null}
                        {detail.reviewedAt ? (
                          <Typography.Text type="secondary">
                            Reviewed by {detail.reviewedBy?.slice(0, 8) ?? 'AI'} il{' '}
                            {dayjs(detail.reviewedAt).format('DD/MM/YYYY HH:mm')}
                          </Typography.Text>
                        ) : null}
                      </Space>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
        ) : null}
      </Drawer>

      {/* Approve modal with AI override fields */}
      <Modal
        title="Approva scontrino"
        open={approveId !== null}
        onCancel={() => {
          setApproveError(null);
          setApproveId(null);
        }}
        onOk={onApprove}
        okText="Approva"
        confirmLoading={reviewMutation.isPending}
        destroyOnHidden
        width={680}
      >
        <Alert
          type="info"
          showIcon
          message="Modifica i campi AI se necessari prima di approvare. Valori vuoti preservano l'estrazione originale."
          style={{ marginBottom: 16 }}
        />
        {approveError ? (
          <Alert
            type="error"
            showIcon
            message="Approvazione rifiutata"
            description={approveError}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {approveTarget?.aiDate &&
        dayjs().diff(dayjs(approveTarget.aiDate), 'day') > MAX_RECEIPT_AGE_DAYS ? (
          <Alert
            type="error"
            showIcon
            message={`Scontrino datato ${dayjs(approveTarget.aiDate).format(
              'DD/MM/YYYY',
            )} — più vecchio di ${MAX_RECEIPT_AGE_DAYS} giorni. Non approvabile per regolamento.`}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form form={approveForm} layout="vertical">
          <Form.Item
            name="pointsAwarded"
            label="Punti da assegnare"
            rules={[{ required: true, type: 'number', min: 0, max: 1000 }]}
          >
            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>

          {/* T-155 — senza partner l'approvazione viene rifiutata dal server. Se
              la correzione di P.IVA / ragione sociale qui sotto basta ad
              agganciarlo, questo campo può restare vuoto: il server ci riprova
              da solo sui valori corretti. */}
          <Form.Item
            name="sponsorId"
            label="Partner"
            extra="Vuoto = riconosciuto automaticamente da P.IVA o ragione sociale. Scegli a mano solo se il riconoscimento non aggancia. Se l'esercente è un partner non ancora registrato, va prima creato in Sponsor."
          >
            <Select
              allowClear
              showSearch
              placeholder="Riconoscimento automatico"
              optionFilterProp="label"
              options={activeSponsorOptions}
            />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Correzione dati AI
          </Typography.Title>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="aiStoreName" label="Nome negozio">
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="aiBusinessName" label="Ragione sociale">
                <Input maxLength={255} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="aiPartitaIva" label="P.IVA">
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="aiReceiptNumber" label="Numero scontrino">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="aiDate"
                label="Data scontrino"
                extra={`Ammessi solo scontrini degli ultimi ${MAX_RECEIPT_AGE_DAYS} giorni`}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  disabledDate={(d) =>
                    d.isAfter(dayjs(), 'day') ||
                    d.isBefore(dayjs().subtract(MAX_RECEIPT_AGE_DAYS, 'day'), 'day')
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="aiTotalAmount" label="Totale (EUR)">
                <InputNumber
                  min={0}
                  max={100000}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* T-138 — Reopen modal: riapre un RESPINTO rimettendolo in revisione */}
      <Modal
        title="Riapri scontrino respinto"
        open={reopenId !== null}
        onCancel={() => setReopenId(null)}
        onOk={onReopen}
        okText="Riapri (→ revisione)"
        confirmLoading={reopenMutation.isPending}
        destroyOnHidden
        width={560}
      >
        <Alert
          type="info"
          showIcon
          message="Lo scontrino torna in coda di revisione — non viene approvato"
          description="Riaprire NON assegna punti: rimette lo scontrino tra quelli da rivedere. Dopo aver guardato l'immagine, potrai approvarlo (con i punti) dalla coda di revisione. L'azione resta tracciata (chi, quando, motivazione)."
          style={{ marginBottom: 16 }}
        />
        {reopenTarget?.rejectionReason ? (
          <Alert
            type="warning"
            showIcon
            message="Motivo del respingimento"
            description={reopenTarget.rejectionReason}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form form={reopenForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Motivazione della riapertura (obbligatoria)"
            rules={[{ required: true, whitespace: true, min: 1, max: 500 }]}
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder="Perché riapri questo scontrino respinto?"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reject modal with reason presets */}
      <Modal
        title="Respingi scontrino"
        open={rejectId !== null}
        onCancel={() => setRejectId(null)}
        onOk={onReject}
        okText="Respingi"
        okButtonProps={{ danger: true }}
        confirmLoading={reviewMutation.isPending}
        destroyOnHidden
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item label="Motivi rapidi">
            <Space wrap>
              {REJECT_REASON_PRESETS.map((p) => (
                <Button
                  key={p}
                  size="small"
                  onClick={() => rejectForm.setFieldsValue({ rejectionReason: p })}
                >
                  {p}
                </Button>
              ))}
            </Space>
          </Form.Item>
          <Form.Item
            name="rejectionReason"
            label="Motivo (visibile al tifoso nella push)"
            rules={[{ required: true, max: 500 }]}
          >
            <Input.TextArea rows={4} placeholder="es. Scontrino illeggibile, dati troncati..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk reject modal — stesso motivo per tutti gli scontrini selezionati */}
      <Modal
        title={`Respingi ${selectedRowKeys.length} scontrini`}
        open={bulkRejectOpen}
        onCancel={() => setBulkRejectOpen(false)}
        onOk={onBulkReject}
        okText="Respingi tutti"
        okButtonProps={{ danger: true }}
        confirmLoading={bulkLoading}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          message={`Stai per respingere ${selectedRowKeys.length} scontrini con lo stesso motivo.`}
          style={{ marginBottom: 16 }}
        />
        <Form form={bulkRejectForm} layout="vertical">
          <Form.Item label="Motivi rapidi">
            <Space wrap>
              {REJECT_REASON_PRESETS.map((p) => (
                <Button
                  key={p}
                  size="small"
                  onClick={() => bulkRejectForm.setFieldsValue({ rejectionReason: p })}
                >
                  {p}
                </Button>
              ))}
            </Space>
          </Form.Item>
          <Form.Item
            name="rejectionReason"
            label="Motivo (visibile ai tifosi nella push)"
            rules={[{ required: true, max: 500 }]}
          >
            <Input.TextArea rows={4} placeholder="es. Scontrino duplicato, sponsor non aderente..." />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
